/**
 * ConnectInboxPrompt - asks a signed-in teammate who has no inbox to send
 * from to connect their own Gmail, so a new partner is never left inside POE
 * with nothing to read or send until they find Settings > Connected Accounts.
 *
 * "No inbox" means nothing active in the same list the email composer sends
 * from: the person's own mailboxes plus any shared with them. Someone who works
 * from a shared team inbox is never asked.
 */

import { useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useLocation } from 'wouter';
import { Mail, RefreshCw } from 'lucide-react';
import { api, ApiError, queryKeys } from '../lib/api.js';
import { leaveAppTo } from '../lib/browser-navigation.js';
import { useAuth } from '../hooks/useAuth.js';
import { useEmbeddedMode } from '../hooks/useEmbeddedMode.js';

const SKIPPED_KEY_PREFIX = 'poe_connect_inbox_skipped:';
// The page that does the connecting; the prompt would only repeat it there.
const CONNECTED_ACCOUNTS_PATH = '/admin/connected-accounts';

function wasSkipped(userId: string): boolean {
  try {
    return sessionStorage.getItem(SKIPPED_KEY_PREFIX + userId) === '1';
  } catch {
    return false;
  }
}

function rememberSkip(userId: string): void {
  try {
    sessionStorage.setItem(SKIPPED_KEY_PREFIX + userId, '1');
  } catch {
    // Storage can be unavailable; the prompt still closes for this page view.
  }
}

function connectFailureMessage(error: unknown): string {
  if (error instanceof ApiError) {
    const message = (error.data as { message?: unknown } | undefined)?.message;
    if (typeof message === 'string' && message.trim()) return message;
  }
  return "Couldn't open the Google sign-in. Try again, or connect from Settings > Connected Accounts.";
}

export default function ConnectInboxPrompt() {
  const { user, isAuthenticated } = useAuth();
  // Google's sign-in refuses to load inside another site's frame, so the
  // inbox Land Ally AI embeds could never complete a connection.
  const { isStandalone } = useEmbeddedMode();
  const [location] = useLocation();
  const [skippedNow, setSkippedNow] = useState(false);

  const isTeammate = user?.role === 'founder' || user?.role === 'staff';
  const eligible = Boolean(
    isAuthenticated
      && user
      && isTeammate
      && isStandalone
      && !location.startsWith(CONNECTED_ACCOUNTS_PATH)
      && !skippedNow
      && !wasSkipped(user.id),
  );

  const inboxes = useQuery({
    queryKey: queryKeys.connectedAccounts.list(),
    queryFn: () => api.get<{ accounts: Array<{ status: string }> }>('/api/connected-accounts'),
    enabled: eligible,
    staleTime: 60_000,
  });

  const connect = useMutation({
    mutationFn: () => api.post<{ authUrl: string }>(
      '/api/connected-accounts/connect/gmail',
      { suggestOwnAccount: true },
    ),
    onSuccess: ({ authUrl }) => leaveAppTo(authUrl),
  });

  // A failed or unfinished lookup is not evidence of an empty inbox list.
  const hasInbox = inboxes.data?.accounts.some((account) => account.status === 'active') ?? true;
  if (!eligible || !user || !inboxes.isSuccess || hasInbox) return null;

  const skip = () => {
    rememberSkip(user.id);
    setSkippedNow(true);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/40" aria-hidden />
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="connect-inbox-title"
        aria-describedby="connect-inbox-body"
        className="relative bg-surface-raised rounded-2xl shadow-xl p-6 max-w-sm w-full mx-4 border border-line"
      >
        <div className="flex items-center gap-3 mb-3">
          <div className="w-10 h-10 rounded-full bg-surface-sunken flex items-center justify-center">
            <Mail size={18} className="text-red-500" aria-hidden />
          </div>
          <div className="min-w-0">
            <h2 id="connect-inbox-title" className="text-sm font-semibold text-ink">Connect your Gmail</h2>
            <p className="text-xs text-ink-muted truncate">{user.email}</p>
          </div>
        </div>
        <p id="connect-inbox-body" className="text-sm text-ink-muted mb-5">
          POE sends and receives your email through Gmail. Connect it once and your conversations
          show up here. Google will ask you to allow access.
        </p>
        {connect.isError && (
          <p role="alert" className="text-sm text-danger mb-4">{connectFailureMessage(connect.error)}</p>
        )}
        <div className="flex gap-2 justify-end">
          <button
            type="button"
            onClick={skip}
            disabled={connect.isPending}
            className="min-h-[44px] px-4 text-sm rounded-lg border border-line text-ink-muted hover:bg-surface-sunken transition-colors disabled:opacity-60"
          >
            Skip for now
          </button>
          <button
            type="button"
            onClick={() => connect.mutate()}
            disabled={connect.isPending}
            autoFocus
            className="inline-flex min-h-[44px] items-center justify-center gap-1.5 rounded-lg bg-primary px-4 text-sm font-medium text-white transition-colors hover:bg-primary/90 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {connect.isPending ? (
              <>
                <RefreshCw size={12} className="animate-spin" aria-hidden />
                Opening Google...
              </>
            ) : (
              'Connect Gmail'
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
